import React, { useState, useEffect } from "react"
import { Arrow } from "components/page-components/Icons/small"
import { useStaticQuery, graphql } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import {
  validateMailAddress,
  handleEmailSubmit,
  MailSignupResultModal,
} from "components/page-components/Signup-Forms/utils"

const SignupComponent = ({
  button_loading,
  check_enter,
  datenschutz_checked,
  email_input,
  handle_email_input,
  handle_submit,
  mail_signup_result_modal_state,
  ressort,
  ressort_mapping,
  set_button_loading,
  set_mail_signup_result_modal_state,
  toggle_datenschutz,
  valid_email,
}) => (
  <React.Fragment>
    <div
      data-nosnippet
      className={`box pb-0 is-shadowless has-background-${ressort_mapping[ressort]?.bg_color} mb-5`}
      style={{ minHeight: 290 }}
    >
      <div className="columns is-flex-direction-row-reverse">
        <div className="column is-flex is-flex-direction-column">
          <p className="title is-4 has-text-white pt-4">
            {ressort_mapping[ressort]?.title}
          </p>
          <p className="has-text-white" style={{ fontSize: 18 }}>
            {ressort_mapping[ressort]?.description}
          </p>
          <p
            className="has-text-white mt-auto mb-4 has-text-weight-bold"
            style={{ fontSize: 18 }}
          >
            {ressort_mapping[ressort]?.cta}
          </p>

          <div className="field has-addons pb-4">
            <div className="control is-expanded">
              <input
                type="text"
                className="input has-text-mitternachtsblau"
                placeholder="Ihre E-Mail Adresse"
                onChange={handle_email_input}
                onKeyDown={check_enter}
                value={email_input}
              />
            </div>
            <div className="control">
              <button
                disabled={!valid_email || !datenschutz_checked}
                onClick={handle_submit}
                className={`button is-sonnengelb email-signup ${
                  button_loading ? "is-loading" : ""
                }`}
              >
                {button_loading ? null : (
                  <span className="icon">
                    <Arrow {...{ size: 0.8 }} />
                  </span>
                )}
              </button>
            </div>
          </div>

          <div
            style={{
              transition: `.2s ease`,
              maxHeight: valid_email ? "200px" : "0px",
              opacity: valid_email ? "1" : "0",
              visibility: valid_email ? "visible" : "hidden",
            }}
          >
            <div className="is-flex checkbox-item">
              <div
                onClick={toggle_datenschutz}
                aria-label="Datenschutzbestimmungen akzeptieren"
                role="button"
                tabIndex="-1"
                onKeyDown={toggle_datenschutz}
                className={`checkbox${
                  datenschutz_checked ? " is-refined" : ""
                }`}
              ></div>
              <span
                onClick={toggle_datenschutz}
                aria-label="Datenschutzbestimmungen akzeptieren"
                role="button"
                tabIndex="-2"
                onKeyDown={toggle_datenschutz}
                className="my-auto has-text-white is-clickable has-text-left help"
              >
                Ich akzeptiere die{" "}
                <a
                  href="/datenschutzerklaerung"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link has-text-white"
                >
                  Datenschutz-Bestimmungen
                </a>
                .
              </span>
            </div>
          </div>
        </div>
        <div className="column is-5 pb-0 is-flex">
          <GatsbyImage
            {...{
              image:
                ressort_mapping[ressort]?.image.childImageSharp.gatsbyImageData,
              alt: "Newsletter Anmeldung",
              title: "Newsletter Anmeldung",
              className: "is-hidden-mobile mt-auto mx-auto",
            }}
          />
          <GatsbyImage
            {...{
              image:
                ressort_mapping[ressort]?.image_mobile.childImageSharp
                  .gatsbyImageData,
              alt: "Newsletter Anmeldung",
              title: "Newsletter Anmeldung",
              className: "is-hidden-tablet mt-auto mx-auto",
            }}
          />
        </div>
      </div>
    </div>
    <MailSignupResultModal
      {...{
        mail_signup_result_modal_state,
        set_button_loading,
        set_mail_signup_result_modal_state,
      }}
    />
  </React.Fragment>
)

export const NewsletterSignupBoxed = ({
  ressort = "baumagazin",
  in_blog = false,
  modal = false,
  close_exit_intent_modal,
  adbutler_page_id,
}) => {
  const {
    desktop: { devices_desktop },
    mobile: { devices_mobile },
  } = useStaticQuery(graphql`
    query {
      desktop: allFile(
        filter: {
          relativeDirectory: { eq: "newsletter-signup-devices/in-article" }
          name: { regex: "/^(?!.*mobile)/" }
        }
      ) {
        devices_desktop: nodes {
          name
          childImageSharp {
            gatsbyImageData(
              formats: [AUTO, AVIF]
              height: 270
              layout: CONSTRAINED
              placeholder: BLURRED
            )
          }
        }
      }
      mobile: allFile(
        filter: {
          relativeDirectory: { eq: "newsletter-signup-devices/in-article" }
          name: { regex: "/mobile/" }
        }
      ) {
        devices_mobile: nodes {
          name
          childImageSharp {
            gatsbyImageData(
              formats: [AUTO, AVIF]
              width: 320
              layout: CONSTRAINED
              placeholder: BLURRED
            )
          }
        }
      }
    }
  `)

  const ressort_mapping = {
    baumagazin: {
      title: "Im Bau kennen wir uns aus!",
      description:
        "Für Sie bauen wir unseren Newsletter mit den relevantesten Neuigkeiten aus der Branche.",
      cta: "Gleich abonnieren!",
      abbrev: "bm",
      image: devices_desktop.filter(({ name }) => /baumagazin/.test(name))?.[
        (adbutler_page_id || Math.floor(Math.random() * 2)) % 2
      ],
      image_mobile: devices_mobile.filter(({ name }) =>
        /baumagazin/.test(name)
      )?.[(adbutler_page_id || Math.floor(Math.random() * 2)) % 2],
      bg_color: "baumagazin",
    },
    galabau: {
      title: "Gedeiht die grüne Branche?",
      description:
        "Aktuelle Nachrichten zu den Entwicklungen im GaLa-Bau erfahren Sie in unserem Newsletter.",
      cta: "Hier abonnieren!",
      abbrev: "gb",
      image: devices_desktop.filter(({ name }) => /galabau/.test(name))?.[
        (adbutler_page_id || Math.floor(Math.random() * 2)) % 2
      ],
      image_mobile: devices_mobile.filter(({ name }) => /galabau/.test(name))?.[
        (adbutler_page_id || Math.floor(Math.random() * 2)) % 2
      ],
      bg_color: "galabau",
    },
    umweltbau: {
      title: "Rohrpost abonnieren!",
      description:
        "Wir graben für Sie nach Neuigkeiten. Die Ergebnisse gibt es bei uns im Newsletter.",
      cta: "Jetzt anmelden!",
      abbrev: "ub",
      image: devices_desktop.filter(({ name }) => name === "umweltbau")?.[0],
      image_mobile: devices_mobile.filter(
        ({ name }) => name === "umweltbau-mobile"
      )?.[0],
      bg_color: "umweltbau",
    },
    evergabe: {
      title: "Mehr aus der Vergabe!",
      description:
        "Regelmäßigen Input zu Neuigkeiten erhalten Sie mit unserem Newsletter.",
      cta: "Hier anmelden!",
      abbrev: "ad",
      image: devices_desktop.filter(({ name }) => name === "evergabe")?.[0],
      image_mobile: devices_mobile.filter(
        ({ name }) => name === "evergabe-mobile"
      )?.[0],
      bg_color: "mitternachtsblau",
    },
  }

  const [button_loading, set_button_loading] = useState(false)
  const [valid_email, set_valid_email] = useState(false)
  const [email_input, set_email_input] = useState("")
  const [datenschutz_checked, set_datenschutz_checked] = useState(false)
  const toggle_datenschutz = () => set_datenschutz_checked(!datenschutz_checked)

  const [mail_signup_result_modal_state, set_mail_signup_result_modal_state] =
    useState({ is_active: false, request_was_successfull: true })

  const handle_email_input = (e) => {
    set_email_input(e.target.value)
  }
  const check_enter = (e) => e.key === "Enter" && handle_submit()

  const handle_submit = async () => {
    set_button_loading(true)
    handleEmailSubmit({
      list: "newsletter",
      email: email_input,
      interests: [ressort_mapping[ressort].abbrev],
      launchModal,
    })
  }
  const launchModal = (success) => {
    if (success && !!close_exit_intent_modal) {
      close_exit_intent_modal()
    }
    set_mail_signup_result_modal_state({
      is_active: true,
      request_was_successfull: success,
    })
  }
  useEffect(() => {
    set_valid_email(validateMailAddress(email_input))
  }, [email_input])

  return in_blog || modal ? (
    <SignupComponent
      {...{
        button_loading,
        check_enter,
        datenschutz_checked,
        email_input,
        handle_email_input,
        handle_submit,
        mail_signup_result_modal_state,
        ressort,
        ressort_mapping,
        set_button_loading,
        set_mail_signup_result_modal_state,
        toggle_datenschutz,
        valid_email,
      }}
    />
  ) : (
    <div className={`columns is-mobile is-centered`}>
      <div className="column is-9-desktop is-12-touch">
        <SignupComponent
          {...{
            button_loading,
            check_enter,
            datenschutz_checked,
            email_input,
            handle_email_input,
            handle_submit,
            mail_signup_result_modal_state,
            ressort,
            ressort_mapping,
            set_button_loading,
            set_mail_signup_result_modal_state,
            toggle_datenschutz,
            valid_email,
          }}
        />
      </div>
    </div>
  )
}
